import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { toast } from "react-hot-toast";
import {
  useRequestsApi,
  type RequestResponse,
} from "../../../hooks/useRequestsApi";
import AssignedRequests from "./AssignedRequests";
import AssignSlotsModal from "./AssignSlotsModal";
import CreateMeetingModal from "./CreateMeetingModal";
import RequestDetailsModal from "./RequestDetailsModal";
import AddMeetLinkModal from "./AddMeetLinkModal";

const TeacherExplanations: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = React.useState("");
  const [assignedRequests, setAssignedRequests] = React.useState<
    RequestResponse[]
  >([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = React.useState(false);
  const [showCreateMeetingModal, setShowCreateMeetingModal] =
    React.useState(false);
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [showAddMeetLinkModal, setShowAddMeetLinkModal] = React.useState(false);
  const [selectedRequest, setSelectedRequest] =
    React.useState<RequestResponse | null>(null);

  // Utiliser le nouveau hook pour l'API
  const { getMyRequests, proposeSlots } = useRequestsApi();

  // Charger les données du professeur
  const fetchTeacherData = async () => {
    try {
      setLoading(true);
      const response = await getMyRequests();

      if (response.status === 200 && Array.isArray(response.body)) {
        setError(null);
        const allRequests = response.body;
        setAssignedRequests(allRequests);
      } else {
        setError("Erreur lors de la récupération des demandes.");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      setError("Une erreur s'est produite lors de la connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchTeacherData();
  }, []); // Dépendances vides pour éviter la boucle infinie

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setSelectedStatus(event.target.value);
  };

  // Ouvrir la modale d'assignation de créneaux
  const handleAssignSlots = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowAssignModal(true);
  };

  // Ouvrir le modal pour ajouter/modifier le lien de meet
  const handleAddMeetLink = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowAddMeetLinkModal(true);
  };

  // Ouvrir la modale de création de réunion
  const handleCreateMeeting = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowCreateMeetingModal(true);
  };

  // Ouvrir la modale de détails
  const handleViewDetails = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Assigner des créneaux
  const handleAssignTimeSlots = async (
    slots: { startTime: Date; endTime: Date }[],
  ) => {
    if (!selectedRequest) return;

    try {
      // L'API attend un tableau de strings ISO (dates de début uniquement)
      const response = await proposeSlots(selectedRequest._id, {
        slots: slots.map((slot) => slot.startTime.toISOString()),
      });
      console.log("📨 Réponse proposeSlots:", response);

      // Vérifier si la réponse est un succès (200, 201)
      if (response.status === 200 || response.status === 201) {
        // Vérifier si le body contient un message d'erreur
        if (
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body &&
          !("_id" in response.body)
        ) {
          toast.error(
            (response.body.message as string) ||
              "Erreur lors de la proposition",
          );
          return;
        }

        // Succès
        toast.success(
          "Créneaux proposés avec succès ! Vous pouvez maintenant ajouter le lien Google Meet.",
        );

        // Recharger les données
        await fetchTeacherData();

        // Fermer la modale d'assignation et ouvrir la modale de création de réunion
        setShowAssignModal(false);
        setShowCreateMeetingModal(true);
        // Ne pas réinitialiser selectedRequest pour garder la référence pour la création de réunion
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de la proposition des créneaux";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Exception:", error);
      toast.error(
        error?.message || "Erreur lors de la proposition des créneaux",
      );
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "validated":
        return "bg-orange-100 text-orange-800";
      case "slots_proposed":
        return "bg-blue-100 text-blue-800";
      case "student_confirmed":
        return "bg-indigo-100 text-indigo-800";
      case "meeting_scheduled":
        return "bg-emerald-100 text-emerald-800";
      case "scheduled":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "validated":
        return "Nouvelle assignation";
      case "slots_proposed":
        return "Créneaux proposés";
      case "student_confirmed":
        return "Créneau confirmé";
      case "meeting_scheduled":
        return "Réunion programmée";
      case "scheduled":
        return "Séance programmée";
      case "completed":
        return "Terminée";
      default:
        return "Statut inconnu";
    }
  };

  const getFilteredRequests = () => {
    if (!assignedRequests) return [];

    let filteredRequests = assignedRequests;

    // Filtrer par statut spécifique (si sélectionné)
    if (selectedStatus) {
      filteredRequests = filteredRequests.filter(
        (r) => r.status === selectedStatus,
      );
    }

    return filteredRequests.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: "full",
          margin: "auto",
          bgcolor: "background.paper",
          p: { xs: 1, sm: 2 },
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            Mes assignations d'explications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consultez toutes les demandes qui vous ont été assignées
          </Typography>
        </Box>

        {/* Filtre de statut uniquement */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <FormControl
            variant="standard"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              "& .MuiInput-root": {
                fontSize: { xs: "0.875rem", sm: "1rem" },
              },
            }}
          >
            <Select
              value={selectedStatus}
              onChange={handleStatusChange}
              displayEmpty
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" },
                "& .MuiSelect-select": {
                  padding: { xs: "8px 12px", sm: "12px 16px" },
                },
              }}
            >
              <MenuItem
                value=""
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Tous les statuts
              </MenuItem>
              <MenuItem
                value="validated"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Nouvelle assignation
              </MenuItem>
              <MenuItem
                value="slots_proposed"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Créneaux proposés
              </MenuItem>
              <MenuItem
                value="student_confirmed"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Créneau confirmé
              </MenuItem>
              <MenuItem
                value="meeting_scheduled"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Réunion programmée
              </MenuItem>
              <MenuItem
                value="scheduled"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Programmée
              </MenuItem>
              <MenuItem
                value="completed"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Terminée
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Liste des demandes filtrées */}
        <div className="space-y-2">
          {getFilteredRequests().length === 0 ? (
            <p className="text-gray-500 text-center text-sm">
              Aucune demande trouvée.
            </p>
          ) : (
            <AssignedRequests
              requests={getFilteredRequests()}
              onProposeSlots={handleAssignSlots}
              onViewDetails={handleViewDetails}
              onAddMeetLink={handleAddMeetLink}
              getStatusBadgeColor={getStatusBadgeColor}
              getStatusText={getStatusText}
            />
          )}
        </div>
      </Box>

      {/* Modales */}
      <Box
        sx={{ display: "flex", justifyContent: "center", mt: 4, zIndex: 9999 }}
      >
        {showAssignModal && selectedRequest && (
          <AssignSlotsModal
            open={showAssignModal}
            request={selectedRequest}
            onAssign={handleAssignTimeSlots}
            onClose={() => {
              setShowAssignModal(false);
              setSelectedRequest(null);
            }}
          />
        )}

        {showCreateMeetingModal && selectedRequest && (
          <CreateMeetingModal
            requestId={selectedRequest._id}
            open={showCreateMeetingModal}
            onClose={() => {
              setShowCreateMeetingModal(false);
              setSelectedRequest(null);
            }}
            onMeetingCreated={() => {
              fetchTeacherData();
              setShowCreateMeetingModal(false);
            }}
          />
        )}

        {showDetailsModal && selectedRequest && (
          <RequestDetailsModal
            open={showDetailsModal}
            request={selectedRequest}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedRequest(null);
            }}
            getStatusBadgeColor={getStatusBadgeColor}
            getStatusText={getStatusText}
          />
        )}

        {showAddMeetLinkModal && selectedRequest && (
          <AddMeetLinkModal
            open={showAddMeetLinkModal}
            request={selectedRequest}
            onClose={() => {
              setShowAddMeetLinkModal(false);
              setSelectedRequest(null);
            }}
            onSuccess={() => {
              // Mise à jour optimiste - pas de rechargement
              toast.success("Lien ajouté avec succès !");
            }}
          />
        )}
      </Box>
    </>
  );
};

export default TeacherExplanations;
